import React from 'react';
import {
  Cog6ToothIcon,
  UserCircleIcon,
  BellIcon
} from '@heroicons/react/24/outline';

export const SettingsGuidePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <Cog6ToothIcon className="w-8 h-8 text-[#FF5722]" />
              <h1 className="text-3xl font-bold text-gray-900">
                Settings Guide
              </h1>
            </div>
            <p className="mt-2 text-sm text-gray-600 max-w-2xl">
              Learn how to manage your account profile and notification preferences so you stay in
              control of your information and alerts across the platform.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Part 1
            </h2>
            <p className="text-base font-medium text-gray-900">
              Update your account details
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Use the Account Settings page to manage your personal info, company and password.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Part 2
            </h2>
            <p className="text-base font-medium text-gray-900">
              Control notifications
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Use the Notifications page to view in‑app alerts and configure preferences.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Part 3
            </h2>
            <p className="text-base font-medium text-gray-900">
              Keep your account secure
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Regularly review your details, passwords and alerts to maintain a secure profile.
            </p>
          </div>
        </div>

        {/* Account Settings section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <UserCircleIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">
                1. Manage your profile (Account Settings)
              </h3>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>
                Open <span className="font-medium">Account Settings</span> from the Settings area
                (route <span className="font-mono">/settings/account</span>, implemented in{' '}
                <span className="font-mono">AccountPage.tsx</span>).
              </li>
              <li>
                At the top of the page you&apos;ll see key identifiers:
                <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                  <li>
                    <span className="font-medium">Customer ID</span>: your unique user ID from the
                    backend.
                  </li>
                  <li>
                    <span className="font-medium">Login name</span>: the email used to log into the
                    platform.
                  </li>
                </ul>
              </li>
              <li>
                In the <span className="font-medium">Account status</span> section you&apos;ll find
                cards for:
                <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                  <li>
                    <span className="font-medium">Identity verification</span>: explains how free
                    credits are limited to your own phone number until you verify.
                  </li>
                  <li>
                    <span className="font-medium">Account verification</span>: describes daily SMS
                    limits and the &quot;Lift limits&quot; option to increase them.
                  </li>
                </ul>
                These actions may integrate with backend verification flows depending on your
                environment.
              </li>
              <li>
                In <span className="font-medium">Personal Information</span>, you can edit:
                <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                  <li>First Name and Last Name.</li>
                  <li>Email Address (stored as your company email on the backend).</li>
                  <li>Phone Number used for contact and verification.</li>
                  <li>
                    Company (populated from <span className="font-mono">/api/users/companies</span>;
                    your current company is ensured to appear even if not yet in the list).
                  </li>
                </ul>
              </li>
              <li>
                Update your details and click <span className="font-medium">Save Changes</span>. The
                frontend sends a <span className="font-mono">PUT /api/users/:id</span> request with
                the updated profile fields.
              </li>
            </ol>
            <p className="mt-4 text-xs text-gray-500">
              After a successful save, Account Settings reloads your user record from the backend so
              the on‑screen values always reflect what is stored in the database.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">2. Change your password</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>
                Scroll to the <span className="font-medium">Change Password</span> section on the
                Account Settings page.
              </li>
              <li>
                Enter your <span className="font-medium">Current Password</span>, then choose a{' '}
                <span className="font-medium">New Password</span> and confirm it.
              </li>
              <li>
                The page validates that new and confirm passwords match and that a current password
                is provided.
              </li>
              <li>
                On save, if a new password is present, the app calls{' '}
                <span className="font-mono">POST /api/users/:id/change-password</span> with your
                current and new password. Backend validation ensures only the correct current
                password can be changed.
              </li>
              <li>
                If the password update succeeds, the password fields are cleared, your profile data
                is refreshed, and a success message is shown at the top of the page.
              </li>
            </ol>
            <p className="mt-4 text-xs text-gray-500">
              For best security, choose a strong, unique password and update it regularly, especially
              after granting or revoking access for team members who may share credentials.
            </p>
          </div>
        </div>

        {/* Notifications section */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center space-x-3 mb-4">
            <BellIcon className="w-7 h-7 text-[#FF5722]" />
            <h3 className="text-lg font-semibold text-gray-900">
              3. Work with notifications (Notifications page)
            </h3>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            The <span className="font-medium">Notifications</span> page (
            <span className="font-mono">NotificationsPage.tsx</span>, route{' '}
            <span className="font-mono">/settings/notifications</span>) helps you read, filter and
            configure alerts generated by the platform.
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
            <li>
              Open <span className="font-medium">Notifications</span> from Settings. At the top
              you&apos;ll see:
              <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                <li>
                  A <span className="font-medium">filter</span> to show All, Unread or Read
                  notifications.
                </li>
                <li>
                  A <span className="font-medium">Refresh</span> button, which reloads notifications
                  from <span className="font-mono">GET /api/notifications/user</span>.
                </li>
              </ul>
            </li>
            <li>
              In the notification list:
              <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                <li>
                  Unread notifications are highlighted (blue background and &quot;New&quot; badge).
                </li>
                <li>
                  The colored dot on the left indicates type (success, failure, info).
                </li>
                <li>
                  Each notification shows title, a short message preview, optional action and the
                  created date/time.
                </li>
              </ul>
            </li>
            <li>
              Click a notification to open its <span className="font-medium">expanded view</span>.
              Here you can read the full message, see action details and:
              <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                <li>
                  Toggle <span className="font-medium">Mark as read / unread</span>. This updates
                  the UI immediately and calls{' '}
                  <span className="font-mono">POST /notifications/:id/read</span> or{' '}
                  <span className="font-mono">/unread</span> on the backend.
                </li>
                <li>
                  <span className="font-medium">Delete</span> the notification, which issues a{' '}
                  <span className="font-mono">DELETE /notifications/:id</span> API call and removes
                  it from the list.
                </li>
              </ul>
            </li>
          </ol>
        </div>

        {/* Notification preferences */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            4. Configure notification preferences
          </h3>
          <p className="text-sm text-gray-700 mb-3">
            At the bottom of the Notifications page, the{' '}
            <span className="font-medium">Notification Preferences</span> section lets you tailor
            what alerts you receive and how often:
          </p>
          <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
            <li>
              Each preference (Low Balance, Rate Changes, Reports &amp; Analytics, Campaign Updates,
              System Alerts, Account Updates) has:
              <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                <li>A title and description explaining when it triggers.</li>
                <li>
                  An <span className="font-medium">Enabled</span> toggle (slider) to turn that
                  preference on or off.
                </li>
                <li>
                  A <span className="font-medium">Notification Frequency</span> dropdown (Instant,
                  Daily Digest, Weekly Digest, Never).
                </li>
              </ul>
            </li>
            <li>
              Changing the toggle or frequency updates local state immediately and then calls{' '}
              <span className="font-mono">POST /notifications/preferences</span> with the full list
              of preferences so the backend can persist them.
            </li>
            <li>
              Selecting <span className="font-mono">Never</span> for a preference disables it and
              ensures you won&apos;t receive that category of alerts until you re‑enable it.
            </li>
            <li>
              A help note at the bottom explains that instant notifications are sent immediately,
              while digest options bundle multiple events together (for example, daily or weekly
              emails or in‑app summaries).
            </li>
          </ul>
          <p className="mt-4 text-xs text-gray-500">
            Use these controls to balance awareness with noise: keep critical alerts (like system
            issues and low balance) set to Instant, and move informational items to daily or weekly
            digests.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SettingsGuidePage;


